/**
 * @fileoverview Consolidated Application Provider for Clockwise CPA
 *
 * This provider consolidates multiple context providers into a single, manageable component:
 * - OrganizationProvider: Multi-tenant organization configuration
 * - UtilsProvider: Utility functions and user settings
 * - NotifyProvider: Application notification management
 * - AuthProvider: User authentication and session management
 * - SearchProvider: Global search functionality
 * - AccessibilityProvider: Accessibility settings and preferences
 * - NavigationProvider: Navigation state and breadcrumbs
 *
 * Benefits:
 * - Reduces provider nesting complexity
 * - Maintains proper dependency order
 * - Centralizes context initialization
 * - Improves code organization and maintainability
 * - Provides single import for all app contexts
 *
 * @author Clockwise CPA Development Team
 * @version 2.0.0
 */

import React from 'react';
import { OrganizationProvider } from './OrganizationContext';
import { UtilsProvider } from './UtilContext';
import { NotifyProvider } from './NotificationContext';
import { AuthProvider } from './AuthContext';
import { SearchProvider } from './SearchContext';
import { AccessibilityProvider } from './AccessibilityContext';
import { NotificationCenterProvider } from './NotificationCenterContext';

/**
 * Consolidated Application Provider
 *
 * Wraps children with all necessary context providers in the correct dependency order:
 * 1. OrganizationProvider - Organization config (no dependencies)
 * 2. UtilsProvider - Utility functions (no dependencies)
 * 3. NotifyProvider - Notifications (no dependencies)
 * 4. AuthProvider - Authentication (depends on UtilsProvider)
 * 5. NotificationCenterProvider - Persistent notifications (depends on AuthProvider)
 * 6. AccessibilityProvider - Accessibility (no dependencies)
 * 7. SearchProvider - Search (depends on AuthProvider)
 *
 * Note: NavigationProvider is excluded because it requires Router context
 *
 * @param {Object} props - Component props
 * @param {React.ReactNode} props.children - Child components to wrap
 * @returns {JSX.Element} Nested context providers
 */
export const AppProvider = ({ children }) => {
  return (
    <OrganizationProvider>
      <UtilsProvider>
        <NotifyProvider>
          <AuthProvider>
            <NotificationCenterProvider>
              <AccessibilityProvider>
                <SearchProvider>
                  {children}
                </SearchProvider>
              </AccessibilityProvider>
            </NotificationCenterProvider>
          </AuthProvider>
        </NotifyProvider>
      </UtilsProvider>
    </OrganizationProvider>
  );
};

/**
 * Re-export all context hooks for convenience
 * This allows importing all context hooks from a single location
 */
export { useOrganization } from './OrganizationContext';
export { useUtils } from './UtilContext';
export { useNotify } from './NotificationContext';
export { useAuth } from './AuthContext';
export { useNotificationCenter } from './NotificationCenterContext';
export { useSearch } from './SearchContext';
export { useAccessibility } from './AccessibilityContext';
// NavigationProvider must be used inside Router context
// export { useNavigation } from './NavigationContext';

/**
 * Example usage of individual context hooks:
 *
 * @example
 * import { useAuth, useNotify, useUtils, useSearch } from './context/AppProvider';
 *
 * function MyComponent() {
 *   const { user } = useAuth();
 *   const { notify } = useNotify();
 *   const { formatCurrency } = useUtils();
 *   const { searchQuery } = useSearch();
 *
 *   // Use context values...
 * }
 */